import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { apiClient } from '../../api/client';
import { getVerificationStatusString } from '../../utils/stripeUtils';
import './VerificationStatus.css';

interface VerificationStatusProps {
  className?: string;
}

const VerificationStatus: React.FC<VerificationStatusProps> = ({ className }) => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [verificationStatus, setVerificationStatus] = useState<string | null>(null);
  const [verificationRequirements, setVerificationRequirements] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchVerificationStatus = async () => {
      try {
        const response = await apiClient.stripe.getVerificationStatus();
        // Convert numeric enum value to string if needed
        setVerificationStatus(getVerificationStatusString(response.status));
        setVerificationRequirements(response.requirements || []);
      } catch (error) {
        console.error('Error fetching verification status:', error);
      }
    };

    if (currentUser?.stripeDetails?.stripeConnectedAccountId) {
      fetchVerificationStatus();
    }
  }, [currentUser]);

  const handleClick = async () => {
    setIsLoading(true);
    try {
      // Navigate to onboarding screen to complete verification
      navigate('/onboarding');
    } catch (error) {
      console.error('Error navigating to onboarding:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (!currentUser?.stripeDetails?.stripeConnectedAccountId) {
    return null;
  }

  // If verification is not required, don't show anything
  if (verificationStatus !== 'Required') {
    return null;
  }

  return (
    <div className={`verification-status-container ${className || ''}`}>
      <div className="verification-status-header">
        <h3>Identity Verification Required</h3>
      </div>
      <div className="verification-status-content">
        <p>Please complete identity verification to continue receiving payments.</p>
        {verificationRequirements.length > 0 && (
          <div className="verification-requirements">
            <p>Required information:</p>
            <ul>
              {verificationRequirements.map((requirement, index) => (
                <li key={index}>{requirement}</li>
              ))}
            </ul>
          </div>
        )}
        <button
          className="verification-button"
          onClick={handleClick}
          disabled={isLoading}
        >
          {isLoading ? 'Loading...' : 'Complete Verification'}
        </button>
      </div>
    </div>
  );
};

export default VerificationStatus;